import { Component, inject, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { take } from "rxjs";
import { Store, select } from "@ngrx/store";

import { ToastService } from "../../../../shared/services/toast/toast.service";
import { MONTH_NAMES } from "../../../../shared/constants/months";
import { PayGroup } from "../../../../shared/models/pay-groups";
import { FileManagementUploadService } from "../../../../shared/services/file-management/file-management-upload.service";
import { Milestone, PayCycle } from "src/app/shared/models/pay-cycle.interface";
import { AppState } from "@store/models/state.model";
import { getGlobalDashboardFilterState } from "@store/index";
import { GlobalDashboardFilterSelect } from "@store/models/globalDashboardFilterSelection.model";
import { FileListComponent } from "./file-list/file-list.component";
import { PaymentOrderHistoryLog } from "@shared/models/payment-order-history-log.interface";
import { CommonService } from "@modules/dashboard/services/common.service";
import { ExternalCrumb } from "@shared/models/breadcrumbs.interface";
import { UpdateHistoryStateInfoAction } from "@store/actions/historyStateInfo.action";
import { FilterMilestoneTypes } from "@modules/global-dashboard/models/global-dashboard-interface";

@Component({
	selector: "app-file-history",
	templateUrl: "./file-history.component.html",
	styleUrls: ["./file-history.component.scss"]
})
export class FileHistoryComponent implements OnInit {
	@ViewChild(FileListComponent) paymentOrderFileHistoryList!: FileListComponent;

	canUploadFiles = false;
	payGroup!: PayGroup;
	payCycleId = "";
	payCycleExternalId = "";
	payrollCycleId = "";

	bannerShow = false;
	historyLogShow = false;

	bannerShowCancel = false;
	bannerShowReset = false;
	milestone!: Milestone;
	payCycle!: PayCycle;
	kpiSelected!: string;
	payCycleName = "";
	pageTitle = "Payment Orders";
	subTitle = "";

	isShowLoadmore = false;
	reachedAllFileHistory = false;
	currentFileHistoryListCount = 0;
	previousUrl = "";
	historyLogDatasource!: PaymentOrderHistoryLog;

	/* Breadcrumbs */
	headerCrumb = "";
	externalCrumbs: ExternalCrumb[] = [];
	milestones!: Milestone[];

	private fileManagmentService = inject(FileManagementUploadService);
	private toastService = inject(ToastService);
	private router = inject(Router);
	private store = inject<Store<AppState>>(Store);
	private commonService = inject(CommonService);

	ngOnInit(): void {
		this.pageTitle = "File Management History";

		if (history.state) {
			this.payGroup = history.state.payGroup;
			this.payCycleId = history.state.payCycleId;
			this.payCycleExternalId = history.state.payCycleExternalId;
			this.payrollCycleId = history.state.payrollCycleId;
			this.milestone = history.state.milestone;
			this.payCycle = history.state.payCycle;
			this.payCycleName = this.payCycle?.name;
			this.previousUrl = history.state.previousUrl;
			this.subTitle = this.commonService.formatMilestonePagesSubtitles(
				history.state.payGroup.legalEntity.data.name,
				history.state.payCycle.name,
				history.state.payrollCycleId,
				history.state.milestone.group
			);
			this.milestones = history.state.milestones;

			this.headerCrumb = history.state.payrollCycleId
				? `${history.state.payGroup.customer.name} - ${history.state.payCycle.name} - ${history.state.payrollCycleId}, GROUP ${history.state.milestone?.group}`
				: `${history.state.payGroup.customer.name} - ${history.state.payCycle.name}, GROUP ${history.state.milestone?.group}`;

			this.externalCrumbs = [
				{
					crumb: "Payment Orders",
					dropDownData: [
						{
							id: this.milestones.find(
								milestone =>
									milestone.type === FilterMilestoneTypes.FUNDING &&
									milestone.group === this.milestone.group
							)?.id!,
							name: "Funding Date"
						},
						{
							id: this.milestones.find(
								milestone =>
									milestone.type === FilterMilestoneTypes.PAYMENTS &&
									milestone.group === this.milestone.group
							)?.id!,
							name: "Net Payment"
						}
					],
					url: "/global-dashboard/payment-orders"
				},
				{
					crumb: "File Management History",
					dropDownData: []
				}
			];
		}

		this.store.pipe(take(1), select(getGlobalDashboardFilterState)).subscribe({
			next: (state: GlobalDashboardFilterSelect) => {
				this.kpiSelected = state.globalDashboard.kpiSelected;
				if (this.router.url.includes("global-dashboard") && this.kpiSelected === "Failed Payment Orders")
					this.pageTitle = "Failed Payment orders";
			}
		});
	}

	/**
	 * Route user to other milestone pages
	 * @param id: milestone ID
	 */
	routeToMilestonePage(id?: string): void {
		const selectedMilestone = this.milestones.find(milestone => milestone.id === id)!;
		let route = "global-dashboard";

		switch (selectedMilestone.type) {
			case "FUNDING_DATE":
				this.router.navigate([`/${route}/funding-date`], {
					state: {
						payGroup: this.payGroup,
						payCycleId: this.payCycleId,
						payCycleName: this.payCycleName,
						payCycle: this.payCycle,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId,
						previousUrl: this.router.url
					}
				});

				this.store.dispatch(
					new UpdateHistoryStateInfoAction({
						payGroup: this.payGroup,
						payCycle: this.payCycle,
						payCycleName: this.payCycle.name,
						milestones: this.milestones,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId
					})
				);
				break;

			case "NETS":
				this.router.navigate([`/${route}/payouts`], {
					state: {
						payGroup: this.payGroup,
						payCycleId: this.payCycleId,
						payCycleName: this.payCycleName,
						payCycle: this.payCycle,
						milestones: this.milestones,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId
					}
				});

				this.store.dispatch(
					new UpdateHistoryStateInfoAction({
						payGroup: this.payGroup,
						payCycle: this.payCycle,
						payCycleName: this.payCycle.name,
						milestones: this.milestones,
						milestone: selectedMilestone,
						payrollCycleId: this.payCycle.payrollCycleId
					})
				);
				break;

			default:
				break;
		}
	}

	uploadG2NFile(files: any) {
		try {
			const file: File = files[0];

			const formData = new FormData();
			formData.append("file", file);
			formData.append("payGroupId", this.payGroup.id);
			formData.append("payCycleId", this.payCycleId);
			formData.append("fileType", "EXCEL");
			formData.append("sheetIndex", "0");
			formData.append("headerIndex", "4");

			if (file.name.includes(this.payrollCycleId)) {
				this.fileManagmentService.uploadG2NFile(formData);
			} else {
				if (file.name.includes(this.payCycleExternalId)) {
					this.fileManagmentService.uploadG2NFile(formData);
				} else {
					this.payrollCycleId
						? this.toastService.showWarning(
								`You have selected the wrong file for PayCycle: ${this.payrollCycleId}  or  ${this.payCycleExternalId}`
						  )
						: this.toastService.showWarning(
								`You have selected the wrong file for PayCycle: ${this.payCycleExternalId}`
						  );
				}
			}
		} catch (error) {
			this.toastService.showError("There was an error loading the file. Please try again");
		}
	}

	/**
	 * Uploads CPN deduction file
	 * @param files: CPN Deduction excel file uploaded
	 */
	uploadCPNDeductionFile(files: any) {
		const file: File = files[0];
		const formData = new FormData();

		formData.append("file", file);
		formData.append("payCycleId", this.payCycleId);
		formData.append("fileType", "EXCEL");

		this.fileManagmentService.uploadCPNDeductionFile(formData);
	}

	showResetBanner(show: boolean) {
		this.bannerShow = show;
		this.bannerShowReset = show;
	}

	showCancelBanner(show: boolean) {
		this.bannerShow = show;
		this.bannerShowCancel = show;
	}
	closeBanner(): void {
		this.bannerShow = false;
	}

	title(): string {
		return ` ${MONTH_NAMES[new Date(this.payCycleExternalId).getMonth()].full}/${this.payCycleExternalId}`;
	}

	/**
	 * Get the total number of file history list
	 * @param count: Number of total file history
	 */
	fileHistoryTotalCount(count: number) {
		const hasFiles = count !== 0;
		this.isShowLoadmore = this.currentFileHistoryListCount < count && hasFiles;
		this.reachedAllFileHistory = !this.isShowLoadmore && hasFiles;
	}

	/**
	 * Get current number of file history list
	 * @param count: Current number of file history list
	 */
	fileHistoryCurrentCount(count: number) {
		this.currentFileHistoryListCount = count;
	}

	onViewHistoryLogClick(historyLogDatasource: PaymentOrderHistoryLog) {
		this.historyLogDatasource = historyLogDatasource;
		this.historyLogShow = true;
	}
}
